import { Component, ojs, app, state, v, each } from "modular-openscriptjs";
import { pageInfo, formatDateForHumans, formatThousand, either, scrollToTop, consoleErrorOnLocal } from "../../../helpers";
import { api } from "../../../declarations";
import SolidIcon from "../../Blocks/SolidIcon";
import InfiniteScroll from "../../Blocks/InfiniteScroll";
import dayjs from "dayjs";

const h = app("h");

ojs(SolidIcon);
ojs(InfiniteScroll);

export default class Reviews extends Component {
    businessId = null;
    reviews = state([]);
    loading = state(true);
    sort = state("latest");
    timeframe = state("all"); // all, 7days, 30days, 3months
    avgRating = state(0);
    page = 1;
    hasMore = true;
    loadingMore = false;

    render() {
        const router = app("router");
        this.businessId = router.params.companyId;
        scrollToTop();

        if (this.loading.value) {
            this.fetchReviews();
            this.fetchAverageRating();
        }

        return h.div(
            { class: "min-vh-100 py-5 mb-5 py-md-5" },
            h.div(
                { class: "container" },
                h.div(
                    { class: "mb-5 d-flex align-items-center justify-content-between flex-wrap gap-4" },
                    h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.button(
                            { class: "btn btn-white border shadow-sm rounded-circle p-2", onclick: "history.back()" },
                            h.SolidIcon("arrow-left")
                        ),
                        h.h2({ class: "fw-bold mb-0" }, "Reviews & Ratings")
                    ),
                    h.div(
                        { class: "d-flex gap-3 flex-wrap" },
                        // Timeframe Filter
                        v(this.timeframe, (tf) => 
                            h.select(
                                { 
                                    class: "form-select w-auto rounded border-0 shadow-sm px-4",
                                    style: "height: 48px;",
                                    onchange: (e) => {
                                        this.timeframe.value = e.target.value;
                                        this.loading.value = true;
                                        this.fetchReviews();
                                    }
                                },
                                h.option({ value: "all", selected: tf.value === "all" }, "All Time"),
                                h.option({ value: "7days", selected: tf.value === "7days" }, "Last 7 Days"),
                                h.option({ value: "30days", selected: tf.value === "30days" }, "Last 30 Days"),
                                h.option({ value: "3months", selected: tf.value === "3months" }, "Last 3 Months")
                            ), {
                                class: 'd-block',
                                $class: 'd-block',
                            }
                        ),
                        // Sort Filter
                        v(this.sort, (s) => 
                            h.select(
                                { 
                                    class: "form-select w-auto rounded border-0 shadow-sm px-4",
                                    style: "height: 48px;",
                                    onchange: (e) => {
                                        this.sort.value = e.target.value;
                                        this.loading.value = true;
                                        this.fetchReviews();
                                    }
                                },
                                h.option({ value: "latest", selected: s.value === "latest" }, "Latest First"),
                                h.option({ value: "oldest", selected: s.value === "oldest" }, "Oldest First"),
                                h.option({ value: "best", selected: s.value === "best" }, "Highest Rated"),
                                h.option({ value: "worst", selected: s.value === "worst" }, "Lowest Rated")
                            )
                        )
                    )
                ),

                h.div(
                    { class: "row g-4" },
                    // Summary Card
                    h.div(
                        { class: "col-12 col-lg-4" },
                        h.div(
                            { class: "card border-0 shadow-sm rounded-4 text-center p-5 sticky-top", style: "top: 2rem;" },
                            h.h1({ class: "display-3 fw-bold text-primary mb-2" }, v(this.avgRating, (r) => r.value.toFixed(1))),
                            h.div(
                                { class: "text-warning mb-3 fs-4" },
                                v(this.avgRating, (r) => this.renderStars(r.value))
                            ),
                            h.h6({ class: "fw-bold mb-1" }, "Average Rating"),
                            h.p({ class: "text-muted small mb-0" }, "Based on reviews from the last 3 months")
                        )
                    ),

                    // Reviews List
                    h.div(
                        { class: "col-12 col-lg-8" },
                        v(this.loading, (l) => {
                            if (l.value) return h.div({ class: "text-center py-5" }, h.Spinner());
                            
                            return v(this.reviews, (revs) => {
                                if (revs.value.length === 0) {
                                    return h.div(
                                        { class: "card border-0 shadow-sm rounded-4 p-5 text-center bg-white" },
                                        h.div({ class: "text-muted mb-3 opacity-25" }, h.i({ class: "bi bi-chat-left-dots", style: "font-size: 4rem;" })),
                                        h.h5({ class: "fw-bold" }, "No reviews found"),
                                        h.p({ class: "text-muted mb-0" }, "When customers leave feedback, it will appear here.")
                                    );
                                }

                                return h.InfiniteScroll({
                                    child: h.div(
                                        { 
                                            uuid: `reviews-list-${this.businessId}`,
                                            class: "d-grid gap-3" 
                                        },
                                        ...revs.value.map(review => this.renderReviewItem(review))
                                    ),
                                    callback: (entries, observer, marker) => {
                                        entries.forEach((entry) => {
                                            if (!entry.isIntersecting) return;
                                            this.loadMoreReviews(marker);
                                        });
                                    }
                                });
                            });
                        })
                    )
                )
            )
        );
    }

    renderReviewItem(review) {
        return h.div(
            { class: "card border-0 shadow-sm rounded-4 hover-shadow transition" },
            h.div(
                { class: "card-body p-4" },
                h.div(
                    { class: "d-flex align-items-center justify-content-between mb-4" },
                    h.div(
                        { class: "d-flex align-items-center gap-3" },
                        h.img({
                            src: review.author?.profile_photo_path || "/assets/images/user-avatar.png",
                            class: "rounded-circle border",
                            style: "width: 48px; height: 48px; object-fit: cover;"
                        }),
                        h.div(
                            {},
                            h.h6({ class: "fw-bold mb-0 text-dark" }, review.author?.name || "Anonymous Customer"),
                            h.small({ class: "text-secondary" }, dayjs(review.created_at).fromNow())
                        )
                    ),
                    h.div({ class: "text-warning" }, this.renderStars(review.rating))
                ),
                review.title ? h.h6({ class: "fw-bold mb-2" }, review.title) : null,
                h.p({ class: "text-muted mb-0 lh-base" }, review.review || "No written feedback provided.")
            )
        );
    }

    renderStars(rating) {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(h.i({ class: `bi bi-star${i <= Math.round(rating) ? '-fill' : ''} me-1` }));
        }
        return stars;
    }

    async fetchReviews(append = false) {
        if (!append) {
            this.page = 1;
            this.hasMore = true;
        }

        let params = {
            sort: this.sort.value,
            page: this.page
        };

        if (this.timeframe.value !== 'all') {
            const to = dayjs().format('YYYY-MM-DD HH:mm:ss');
            let from;
            if (this.timeframe.value === '7days') from = dayjs().subtract(7, 'day');
            if (this.timeframe.value === '30days') from = dayjs().subtract(30, 'day');
            if (this.timeframe.value === '3months') from = dayjs().subtract(3, 'month');
            
            params.from = from.format('YYYY-MM-DD HH:mm:ss');
            params.to = to;
        }

        try {
            const res = await api.get(`companies/${this.businessId}/reviews`, params);
            if (res) {
                const newReviews = res.data.reviews.data || [];
                if (append) {
                    this.reviews.value = [...this.reviews.value, ...newReviews];
                } else {
                    this.reviews.value = newReviews;
                }

                if (newReviews.length < 10 || res.data.reviews.current_page >= res.data.reviews.last_page) {
                    this.hasMore = false;
                } else {
                    this.page++;
                }
            }
        } catch (e) {
            consoleErrorOnLocal(e);
        } finally {
            this.loading.value = false;
        }
    }

    async loadMoreReviews(marker) {
        if (!this.hasMore || this.loadingMore) return;
        this.loadingMore = true;

        let spinner = h.Spinner({
            class: "p-3 my-auto mx-auto",
            parent: marker,
            resetParent: true,
        });

        await this.fetchReviews(true);

        spinner.remove();
        this.loadingMore = false;
    }

    async fetchAverageRating() {
        try {
            const res = await api.get(`companies/${this.businessId}/stats/ratings`);
            if (res) {
                this.avgRating.value = res.data.rating;
            }
        } catch (e) {}
    }
}

ojs(Reviews);
